
import CustomTakePhotoCamera from '@/components/dialog/CustomTakePhotoCamera.vue'
import IconFile from '@/components/svg/IconFile.vue'
import IconImage from '@/components/svg/IconImage.vue'
import IconScannedBarcode from '@/components/svg/IconScannedBarcode.vue'
import IconTakePhoto from '@/components/svg/IconTakePhoto.vue'
import { UPDATE_SCANNED_RESULT } from '@/store/actions'
import { baseUrl, customRequest, removeImage } from '@/utils/api'
import { IMAGE_EXTENSION, MAX_IMAGE_SIZE } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { getFilePathFromURL, getMobileOS, isFirefoxOrOperaAgent, makeid, resizeImage } from '@/utils/helpers'
import { localFileUrlForUpload, rememberLocalFile } from '@/utils/localFileUrls'
import mitt from '@/utils/mitt'
import { AxiosRequestConfig } from 'axios'
import {
  ElFile,
  ElUploadProgressEvent,
  ElUploadRequestOptions,
  UploadFile,
} from 'element-plus/lib/el-upload/src/upload.type'
import { EFileKind } from 'smartbarcode-web-core/src/utils/enums/index'
import { isEmpty } from 'smartbarcode-web-core/src/utils/typeChecker'
import { IBarcodeSavedImageStatus, IFile, TError } from 'smartbarcode-web-core/src/utils/types/index'
import { Options, Vue } from 'vue-class-component'
import { Prop, Ref, Watch } from 'vue-property-decorator'

@Options({
  inheritAttrs: false,
  components: {
    IconImage,
    IconFile,
    IconTakePhoto,
    IconScannedBarcode,
    CustomTakePhotoCamera,
  },
  emits: ['uploaded', 'update:modelValue'],
  name: 'InputImageUpload',
})
export default class InputImageUpload extends Vue {
  @Ref() readonly inputUpload!: unknown
  @Ref() readonly triggerFileDialogBtn!: unknown
  @Prop({ type: String }) readonly fieldName!: string
  @Prop({ type: String }) readonly label!: string
  @Prop({ type: String }) readonly fileKind!: string
  @Prop({ type: String }) readonly overlayImage?: string
  @Prop({ type: Boolean }) readonly isShowCounter!: boolean
  @Prop({ type: [String, Number, Array, Object, Date] }) readonly modelValue?: unknown

  imageUrls = new Set()
  listScannedBCFileFromStore: File[] = []
  listResponsedImage = [] as string[]
  totalImagesNeedProcess = 0
  ocrBlobsShown = 0
  fileList = [] as UploadFile[]
  // picked but not yet resolved (resize runs before the request goes out)
  pendingCount = 0
  uploadingCount = 0
  showDialogAlert = false
  isTakePhotoDialog = false
  isBarcodeScanFrame = false

  private removeEmptyStrings() {
    this.imageUrls.forEach((url) => {
      if (url === '' || url == null) {
        this.imageUrls.delete(url)
      }
    })
    this.listResponsedImage = this.listResponsedImage.filter((url) => url !== '' && url != null)
    this.fileList = this.fileList.filter((file) => {
      return file.url !== '' && file.url != null
    })
  }

  get isSupportNativeImageOptions() {
    return getMobileOS() === 'iOS' || isFirefoxOrOperaAgent()
  }

  get imageOption() {
    return this.isSupportNativeImageOptions ? 'image/*' : 'image/*;capture=camera'
  }

  get getImageLabel() {
    return this.$t(this.isImageFileKind ? 'Pictures' : 'file_urls')
  }

  onCaptureImageUpdated(blob: Blob) {
    const uploadFile = new File([blob], `${makeid(16)}.png`, { type: 'image/png' })
    this.$refs.inputUpload.handleStart(uploadFile)
    this.$refs.inputUpload.submit()
  }

  onUploadButtonClicked() {
    if (this.isPhotoOnlyKind) {
      this.isTakePhotoDialog = true
      return
    }

    this.$refs.triggerFileDialogBtn.$el.click()
  }

  get component() {
    return this.isImageFileKind ? IconImage : IconFile
  }

  get isImageFileKind() {
    return [EFileKind.IMAGE, EFileKind.BARCODE_SCAN_FRAME, EFileKind.PHOTO_ONLY].find((key) => this.fileKind === key)
  }

  get isScannedBarcodeFrame() {
    return this.fileKind === EFileKind.BARCODE_SCAN_FRAME
  }

  get isScannedBCAvail() {
    return !isEmpty(this.fileList)
  }

  get fileCount() {
    return Array.from(this.imageUrls).length || 0
  }

  get barcodeId() {
    return this.$store.state.barcode.barcode?.id
  }

  get actionUrl() {
    return `${baseUrl}/barcode/file`
  }

  get scannedBarcodeBlobsFromLocalStorage() {
    if (this.$store.getters.scannedBarcodeBlob && this.$store.getters.scannedBarcodeBlob.length > 0) {
      return this.$store.getters.scannedBarcodeBlob
    }
    const scannedBarcodeBlobsLocal = localStorage.getItem(this.keyBarcodeScanChanged)
    let scannedBarcodeBlobsLocalParsed = null
    if (scannedBarcodeBlobsLocal) {
      try {
        const parsedData = JSON.parse(scannedBarcodeBlobsLocal)
        if (Array.isArray(parsedData) && parsedData.length > 0 && typeof parsedData[0] === 'string') {
          scannedBarcodeBlobsLocalParsed = parsedData.map((base64: string) => this.base64ToBlob(base64))
        } else {
          scannedBarcodeBlobsLocalParsed = parsedData
        }
      } catch (error) {
        console.error('Error parsing localStorage data:', error)
        scannedBarcodeBlobsLocalParsed = null
      }
    }
    this.$store.dispatch(UPDATE_SCANNED_RESULT, scannedBarcodeBlobsLocalParsed)
    return scannedBarcodeBlobsLocalParsed
  }

  get scannedBCFile() {
    const scannedFiles: File[] = []
    const scannedBarcodeBlobs = this.scannedBarcodeBlobsFromLocalStorage
    if (!scannedBarcodeBlobs || scannedBarcodeBlobs.length === 0) {
      return null
    }
    for (let i = 0; i < scannedBarcodeBlobs.length; i++) {
      const scannedBarcodeBlob = scannedBarcodeBlobs[i]
      const newFile = new File([scannedBarcodeBlob], `${this.barcodeId}_${i}.png`, { type: 'image/png' })
      scannedFiles.push(newFile)
    }

    if (this.listScannedBCFileFromStore.length === 0) {
      this.listScannedBCFileFromStore = [...scannedFiles]
    }
    return scannedFiles
  }

  get scannedBarcodeStatus(): Record<string, IBarcodeSavedImageStatus> {
    return this.$store.state.barcode.scannedBarcodeStatus || {}
  }

  @Watch('$store.state.barcode.scannedBarcodeStatus')
  uploadBCImage() {
    if (!this.scannedBarcodeStatus?.[this.fieldName]) return

    if (this.scannedBarcodeStatus?.[this.fieldName]?.isReady && this.isScannedBCAvail && !this.fileList[0].response) {
      if (this.listScannedBCFileFromStore) {
        for (let i = 0; i < this.listScannedBCFileFromStore?.length; i++) {
          const file = this.listScannedBCFileFromStore[i]
          this.$refs.inputUpload.handleStart(file)
        }
      }
      this.$refs.inputUpload.submit()
    } else {
      this.$emit('uploaded', [{ url: 'none' }])
    }
  }

  onDialogClose() {
    this.showDialogAlert = false
  }

  // Helper method to convert Blobs to base64 and save to localStorage
  async saveBlobsToLocalStorage(blobs: Blob[]) {
    if (!blobs || blobs.length === 0) return

    try {
      const base64Array = await Promise.all(blobs.map((blob) => this.blobToBase64(blob)))
      localStorage.setItem(this.keyBarcodeScanChanged, JSON.stringify(base64Array))
    } catch (error) {
      console.error('Error saving blobs to localStorage:', error)
    }
  }

  // Helper method to convert Blob to base64 string
  blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result as string
        resolve(result)
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  // Helper method to convert base64 back to Blob
  base64ToBlob(base64: string): Blob {
    const arr = base64.split(',')
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png'
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n)
    }
    return new Blob([u8arr], { type: mime })
  }

  fileIsImage(file: UploadFile) {
    // stored names come back with the original casing, e.g. photo.PNG
    const ext = (file?.name || '').split('.').pop()?.toLowerCase() || ''
    return IMAGE_EXTENSION.includes(ext)
  }

  async preupload(rawFile: ElFile) {
    // File kind is scan frame so we need to re-assign uuid for uploading after click submit button instead
    if (this.isScannedBCAvail && this.fileKind === EFileKind.BARCODE_SCAN_FRAME) {
      this.totalImagesNeedProcess++
      this.fileList.forEach((element, idx) => {
        if (element.name === rawFile.name) {
          this.fileList[idx].uid = rawFile.uid
        }
      })
    }

    // If not image return original file
    if (!this.isImageFileKind) return rawFile
    try {
      const resizedBlob = (await resizeImage({ maxSize: MAX_IMAGE_SIZE, file: rawFile })) as Blob
      return resizedBlob || rawFile
    } catch (error) {
      return rawFile
    }
  }

  beforeRemove(file: IFile) {
    if (file) {
      this.imageUrls.delete(getFilePathFromURL(file.url || ''))
      this.imageUrls.delete(file.response || '')

      this.fileList = this.fileList.filter((element) => element.url !== file.url)
      // listResponsedImage holds upload paths, never the blob url shown in the list
      const removedPath = file.response || getFilePathFromURL(file.url || '')
      this.listResponsedImage = this.listResponsedImage.filter((element) => element !== removedPath)
      if (this.listScannedBCFileFromStore.length > 0) {
        this.listScannedBCFileFromStore = this.listScannedBCFileFromStore.filter(
          (element) => element.name !== file.name
        )
      }

      // Remove empty strings after filtering
      this.removeEmptyStrings()

      return false
    }
    return true
  }

  async onRemove(file: IFile) {
    try {
      const remoteUrl = file?.response ?? ''
      if (!remoteUrl) return
      await removeImage(remoteUrl)
      this.imageUrls.delete(remoteUrl)
      this.fileList = this.fileList.filter((element) => element.response !== remoteUrl)
      this.listResponsedImage = this.listResponsedImage.filter((element) => element !== remoteUrl)

      // Remove empty strings after filtering
      this.removeEmptyStrings()
    } catch (error) {
      errorHandler(error as TError)
    }
  }

  async onError(error: Error) {
    // distinct handle max size 1015 error on server
    try {
      const msg = JSON.parse(error.message)
      const err = msg.errors?.File?.[0] ?? ''
      if (err.includes('1015')) {
        const byteNum = err.substring(err.indexOf('(') + 1, err.indexOf(')'))
        errorHandler('1015', { val1: byteNum })
      } else {
        errorHandler(err)
      }
    } catch (err) {
      errorHandler(error)
    }
  }

  onChanged(file: IFile, files: UploadFile[]) {
    // scan frames are held back until submit, so they never settle here
    if (!this.isBarcodeScanFrame) {
      if (file.status === 'ready') this.pendingCount++
      else if (file.status === 'success' || file.status === 'error') {
        this.pendingCount = Math.max(0, this.pendingCount - 1)
      }
    }
    if (file.status === 'success' || file.status === 'error') {
      this.totalImagesNeedProcess--
    }
    // Wait for all files upload completed (totalImagesNeedProcess = 0)
    if (this.isScannedBarcodeFrame) {
      if (!this.totalImagesNeedProcess) {
        this.handleSuccessImages(files)
      }
      return
    }
    if (file.status === 'success') {
      this.handleSuccessImages(files)
    }
  }

  handleSuccessImages(files: UploadFile[]) {
    if (this.fileKind !== EFileKind.BARCODE_SCAN_FRAME) {
      this.fileList = files
    }
    const uploadedResponse: { url: string }[] = []
    this.listResponsedImage.forEach((element) => {
      uploadedResponse.push({ url: element })
      this.imageUrls.add(element)
    })
    this.fileList.forEach((element, index) => {
      element.response = this.listResponsedImage[index]
    })

    // Remove empty strings after processing
    this.removeEmptyStrings()

    if (this.isScannedBarcodeFrame) {
      this.$emit('uploaded', uploadedResponse)
    }
  }

  onPreview(file?: IFile) {
    window.open(file?.url || '', '_blank')
  }

  @Watch('imageUrls', { deep: true })
  onImageChange() {
    // Remove empty strings before emitting updates
    this.removeEmptyStrings()

    // the mitt bus only reaches the page-level formModel, so nested forms
    // (ListObject items) need the value through the normal v-model channel
    this.$emit('update:modelValue', Array.from(this.imageUrls))
    mitt.emit('update:trackInfo', { [this.fieldName]: Array.from(this.imageUrls) })
    mitt.emit('update:fileList', { [this.fieldName]: Array.from(this.fileList) })
  }

  // @Watch('$store.getters.scannedBarcodeBlob')
  displayBarcodeImage() {
    if (this.scannedBCFile) {
      this.listResponsedImage = []
      const urlCreator = window.URL || window.webkitURL
      for (let i = 0; i < this.scannedBCFile.length; i++) {
        const file = this.scannedBCFile[i]

        const blobUrl = urlCreator.createObjectURL(file)
        const scannedBarcodeBlobs = this.$store.getters.scannedBarcodeBlob
        this.saveBlobsToLocalStorage(scannedBarcodeBlobs)
        this.fileList.push({
          name: `${this.barcodeId}_${i}.png`,
          url: blobUrl,
        } as UploadFile)
      }
    }
  }

  get barcode() {
    return this.$store.state.barcode.barcode
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  get project() {
    return this.$store.state.project.details
  }

  get keyBarcodeScanChanged() {
    return (
      this.project.code + '.' + this.barcode.id + '.trackingData.' + this.currentTrackPointKey + '.scannedBarcodeBlobs'
    )
  }

  @Watch('scannedBarcodeBlobsFromLocalStorage')
  scannedBCChanged() {
    this.isBarcodeScanFrame = this.fileKind === EFileKind.BARCODE_SCAN_FRAME
    if (this.isBarcodeScanFrame) this.displayBarcodeImage()
  }

  @Watch('$store.getters.customFieldsOcrImagesBlobs')
  customFieldOcrImageChanged() {
    const blobs: Blob[] = this.$store.getters.customFieldsOcrImagesBlobs?.[this.fieldName] ?? []
    if (blobs.length < this.ocrBlobsShown) this.ocrBlobsShown = 0
    const urlCreator = window.URL || window.webkitURL
    for (let i = this.ocrBlobsShown; i < blobs.length; i++) {
      const newFile = new File([blobs[i]], `${this.fieldName}_${i}.png`, { type: 'image/png' })
      this.fileList.push({
        name: `${this.barcodeId}_${i}.png`,
        url: urlCreator.createObjectURL(newFile),
      } as UploadFile)
    }
    this.ocrBlobsShown = blobs.length
  }

  unmounted() {
    mitt.emit('update:uploadingStatus', { [this.fieldName]: false })
  }

  get isPhotoOnlyKind() {
    return this.fileKind === EFileKind.PHOTO_ONLY
  }

  mounted() {
    this.scannedBCChanged()

    this.imageUrls = new Set()
    try {
      if (isEmpty(this.modelValue)) return
      this.fileList.push(
        ...(((this.modelValue as UploadFile[]) || [])
          .map((val) => {
            // files saved from a nested form carry only the upload path, no signed url
            const file = ((typeof val === 'string' ? { path: val } : val) as unknown) as IFile
            const path = file.path || getFilePathFromURL(file.url || '')
            if (!path && !file.url) return null

            // url is for display only -- imageUrls must stay the storage path,
            // it is what gets saved
            const dataFile = {
              // the name drives fileIsImage(); a stored file only has its path
              name: file.name || path.split('/').pop()?.split('?')[0] || '',
              // a fresh url: el-upload revokes whatever it holds when it unmounts
              url: file.url || localFileUrlForUpload(path),
              response: path,
            } as UploadFile
            this.imageUrls.add(path)

            return dataFile
          })
          .filter((file): file is UploadFile => file !== null) || [])
      )

      // Remove any empty strings that might have been added
      this.removeEmptyStrings()
    } catch (error) {
      errorHandler(error as TError)
    }
  }

  onAddScannedBCImg() {
    if (this.scannedBCFile) {
      this.displayBarcodeImage()
    } else {
      this.showDialogAlert = true
    }
  }

  get isUploading(): boolean {
    return this.uploadingCount > 0 || this.pendingCount > 0
  }

  // let the owning form know, so it can refuse to submit a half-uploaded value
  @Watch('isUploading')
  onUploadingChanged(isUploading: boolean) {
    mitt.emit('update:uploadingStatus', { [this.fieldName]: isUploading })
  }

  async httpRequestController(option: ElUploadRequestOptions) {
    this.uploadingCount++
    const formData = new FormData()
    formData.append('barcodeId', this.barcodeId)
    formData.append('file', option.file)
    const request = {
      url: option.action,
      method: 'post',
      headers: option.headers as unknown,

      data: formData,
      onUploadProgress: (progressEvent: ElUploadProgressEvent) => {
        progressEvent.percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        option.onProgress(progressEvent)
      },
      withCredentials: true,
      timeout: 1800000, // 30'
    } as AxiosRequestConfig
    try {
      const response = await customRequest(request)
      const path = response.toString()
      this.listResponsedImage.push(path)
      // the api only hands back a path; keep the bytes we just sent for preview
      rememberLocalFile(path, option.file)
      return response
    } finally {
      this.uploadingCount--
    }
  }
}
